<template>
  <div id="agi-assistant" class="agi-chat-container flex flex-col relative" :class="isDrawerMode ? 'drawer-mode' : ''">
    <!-- Messages area -->
    <div id="chat-container" class="chat-messages-area flex-1 overflow-y-auto px-4" ref="chatContainer">
      <!-- Recommended questions -->
      <div v-if="recommendedQuestions.length > 0" class="py-2.5 flex flex-col items-start w-full gap-2">
        <span class="text-sm secondary_color font-medium">You might want to ask:</span>
        <div class="flex flex-row flex-wrap gap-2">
          <div
            v-for="(question, qIndex) in recommendedQuestions"
            :key="qIndex"
            @click="sendRecommendedQuestion(question)"
            class="px-2.5 py-1 text-sm secondary_color recommended_wrapper cursor-pointer recommendation-stagger"
            :style="{ animationDelay: `${qIndex * 100}ms` }"
          >
            {{ question }}
          </div>
        </div>
      </div>

      <!-- Streaming / thinking state -->
      <div v-if="isStreaming" class="flex flex-row gap-[6px] px-0 py-2 message-enter">
        <div class="w-[28px] h-[28px] rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 text-white" style="background: #8B5CF6">
          IA
        </div>
        <div class="flex flex-col gap-1 min-w-0">
          <div class="flex items-baseline gap-2">
            <span class="text-[15px] font-semibold">I-Assistant</span>
          </div>
          <div v-if="currentToolCall" class="streaming-status text-[15px]">
            {{ currentToolCall }}<span class="dots"></span>
          </div>
          <div v-else-if="!streamingMessage" class="streaming-status text-[15px]">
            Thinking<span class="dots"></span>
          </div>
          <div v-if="streamingMessage" class="text_color text-[15px] break-words whitespace-pre-wrap leading-normal" v-html="convertMarkdownToHTML(streamingMessage)"></div>
        </div>
      </div>

      <div v-if="isTyping && !isStreaming" class="flex flex-row gap-[6px] px-0 py-2">
        <div class="w-[28px] h-[28px] rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 text-white" style="background: #8B5CF6">
          IA
        </div>
        <div class="streaming-status text-[15px] pt-1">Thinking<span class="dots"></span></div>
      </div>

      <!-- Voice transcription -->
      <div v-if="liveTranscript && (voiceState === 'LISTENING' || voiceState === 'PROCESSING')" class="flex flex-row gap-[6px] px-0 py-2">
        <div class="w-[28px] h-[28px] rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 text-white" style="background: #3B82F6">You</div>
        <div class="text-[15px] font-normal italic secondary_color pt-1">{{ liveTranscript }}<span class="dots"></span></div>
      </div>

      <!-- Messages -->
      <div
        v-for="message in chatMessages.slice().reverse()"
        :key="message._id"
        class="flex flex-row gap-[6px] px-0 py-2 message group relative message-enter"
      >
        <!-- Profile icon -->
        <div
          class="w-[28px] h-[28px] rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 text-white"
          :style="{ background: message.direction === 'forward' ? '#3B82F6' : '#8B5CF6' }"
        >
          {{ message.direction === 'forward' ? 'You' : 'IA' }}
        </div>

        <!-- Message content -->
        <div class="flex flex-col gap-1 min-w-0 flex-1">
          <!-- Sender name + timestamp -->
          <div class="flex items-baseline gap-2">
            <span class="text-[15px] font-semibold">{{ message.direction === 'forward' ? 'You' : 'I-Assistant' }}</span>
            <span class="text-[11px] font-normal secondary_color">{{ timeAgo(message.sent_at, false, currentTime) }}</span>
          </div>

          <!-- Thought time -->
          <div v-if="message.direction === 'backward' && message.thought_time" class="thought-time-container">
            <div
              class="thought-time-label"
              :class="{ 'cursor-pointer': message.tools_used && message.tools_used.length > 0 }"
              @click="message.tools_used && message.tools_used.length > 0 && toggleThoughtExpand(message._id)"
            >
              Thought for {{ message.thought_time }}s
              <span v-if="message.tools_used && message.tools_used.length > 0" class="expand-icon" :class="{ 'expanded': expandedThoughts[message._id] }">›</span>
            </div>
            <div v-if="expandedThoughts[message._id] && message.tools_used && message.tools_used.length > 0" class="thought-tools-list">
              <div v-for="(tool, tIdx) in message.tools_used" :key="tIdx" class="thought-tool-item">{{ tool }}</div>
            </div>
          </div>

          <!-- Text -->
          <div class="text-[15px] break-words whitespace-pre-wrap leading-normal formatted_message" v-html="formatMessage(message.message)" @click="handleMessageClick"></div>

          <!-- Images -->
          <div v-if="message.images && message.images.length > 0" class="mt-2 flex flex-col gap-2">
            <img v-for="(imageUrl, imgIdx) in message.images" :key="imgIdx" :src="imageUrl" alt="Generated image" class="rounded-lg max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity" @click="openImageModal(imageUrl)" />
          </div>

          <!-- Table / Chart -->
          <TableView v-if="message.json_data?.table" :table-data="message.json_data" />
          <ChartView v-if="message.json_data?.chart" :chartData="message.json_data" />
        </div>
      </div>
    </div>

    <!-- Input area (matches chats input styling) -->
    <div class="flex-shrink-0 px-4 pb-2">
      <div class="flex flex-col rounded-lg bg_4 border_10">
        <a-textarea
          :id="textareaId"
          :resize="false"
          ref="nameInput"
          class="!resize-none !border-none !bg-transparent !shadow-none mt-[6px] px-3 text-[15px]"
          :auto-size="{ minRows: 1, maxRows: 8 }"
          v-model:value="userInput"
          placeholder="Write a message"
          @keydown="handleKeyDown"
        ></a-textarea>
        <div class="flex justify-between items-center px-3 pb-2 pt-1">
          <div class="flex items-center gap-3">
            <label class="cursor-pointer opacity-60 hover:opacity-100 transition-opacity">
              <input type="file" class="hidden" multiple @change="handleFileAttach" />
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
            </label>
            <a-tooltip placement="top" overlayClassName="action_properties_wrapper_emoji">
              <template v-slot:title>
                <EmojiPicker :native="true" @select="insertEmoji" />
              </template>
              <ThemeImage :icon="imageNames.EMOJI_GREY" class="size-[18px] cursor-pointer opacity-60 hover:opacity-100 transition-opacity" alt="emoji" />
            </a-tooltip>
          </div>
          <div>
            <span v-if="voiceState !== 'IDLE'" class="flex items-center gap-1.5">
              <button @click="endVoice" class="voice_end_btn">
                <span class="waveform_bars"><span class="bar"></span><span class="bar"></span><span class="bar"></span><span class="bar"></span></span>
                End
              </button>
            </span>
            <button v-else-if="userInput.trim() === ''" @click="startVoice" class="flex items-center justify-center h-[32px] px-3 rounded-lg border-0 cursor-pointer flex-shrink-0 transition-opacity hover:opacity-85 bg-[var(--foreground-rgb)] text-[var(--background-color)] text-[13px] font-semibold gap-1.5">
              <ThemeImage :icon="imageNames.VOICE_AI_MIC" alt="voice" class="size-4" />
            </button>
            <button v-else @click="sendMessage" class="flex items-center justify-center h-[32px] px-3 rounded-lg border-0 cursor-pointer flex-shrink-0 transition-opacity hover:opacity-85 bg-[var(--foreground-rgb)] text-[var(--background-color)] text-[13px] font-semibold gap-1.5">
              Send
              <ThemeImage :icon="imageNames.SEND_WHITE" alt="send" class="size-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Image modal -->
    <Teleport to="body">
      <div v-if="showImageModal" class="fixed inset-0 z-[2000] flex items-center justify-center bg-black bg-opacity-75 p-4" @click="closeImageModal">
        <div class="relative max-w-4xl max-h-[90vh]">
          <img :src="selectedImage" alt="Full size image" class="max-w-full max-h-full rounded-lg" />
          <button @click.stop="closeImageModal" class="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70">
            <ThemeImage :icon="imageNames.CLOSE_GREY" alt="close" class="size-6 invert" />
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { timeAgo } from "@/src/utils/utils.js";
import DashboardService from "@/src/api/DashboardService.js";
import ChatService from "@/src/api/ChatService.js";
import { Topics } from "@/src/services/chats/topicManager.js";
import mqttService from "@/src/services/mqtt/mqttService.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import { convertMarkdownToHTML } from "../../utils/markdownUtils.js";
import { getObjectInfoFromURL } from "@/src/utils/htmlUtils.js";
import TableView from "./TableView.vue";
import ChartView from "./ChartView.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { useSSE, SSE_EVENTS } from "@/src/composables/useSSE.js";
import { useVoiceMode } from "@/src/composables/useVoiceMode.js";

const props = defineProps({
  isDrawerMode: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const store = useStore();
const { connect: connectSSE, disconnect: disconnectSSE, isConnected: sseConnected } = useSSE();

const textareaId = props.isDrawerMode ? 'message-area-drawer' : 'message-area-page';

const CONVERSATION_ID_KEY = 'agi_conversation_id';

let intervalId = null;
const currentTime = ref(new Date());
const chatMessages = ref([]);
const recommendedQuestions = ref([]);
const userInput = ref("");

// Voice mode callbacks — called by useVoiceMode from the voice-chat WS
const addUserMessage = (text) => {
  conversationId.value = getOrCreateConversationId();
  pushMessage({ direction: 'forward', message: text, sent_at: new Date() });
  recommendedQuestions.value = [];
  resetStreamingState();
  isStreaming.value = true;
  streamingStartTime.value = Date.now();
  scrollToBottom();
  DashboardService.saveAgiMessage(conversationId.value, null, text, null, null, null, null, 'forward')
    .catch(e => console.error('[Voice] Failed to save user message:', e));
};
const onVoiceDelta = (content) => {
  if (!content) return;
  currentToolCall.value = null;
  streamingMessage.value += content;
  scrollToBottom();
};
const onVoiceComplete = (answer) => {
  if (answer === '__DISCARD__') {
    const len = chatMessages.value.length
    if (len > 0 && chatMessages.value[len - 1].direction === 'backward') {
      chatMessages.value.pop()
    }
    resetStreamingState()
    return
  }
  const rawAnswer = answer || streamingMessage.value;
  const formattedMessage = convertMarkdownToHTML(rawAnswer);
  const thinkingDuration = streamingStartTime.value
    ? Math.round((Date.now() - streamingStartTime.value) / 1000)
    : null;
  thoughtTime.value = thinkingDuration;
  pushMessage({
    message: formattedMessage,
    json_data: null,
    recommended_questions: [],
    images: [],
    web_search_used: false,
    direction: 'backward',
    sent_at: new Date(),
    thought_time: thinkingDuration,
    tools_used: [...streamingToolsUsed.value],
  });
  resetStreamingState();
  scrollToBottom();
  DashboardService.saveAgiMessage(conversationId.value, null, rawAnswer, null, [], [], false, 'backward')
    .catch(e => console.error('[Voice] Failed to save AI response:', e));
};
const onVoiceToolCall = (displayName) => {
  const cleanName = stripEmojis(displayName);
  currentToolCall.value = cleanName;
  if (cleanName && !streamingToolsUsed.value.includes(cleanName)) {
    streamingToolsUsed.value.push(cleanName);
  }
};
const getConversationId = () => conversationId.value || getOrCreateConversationId();

const { voiceState, liveTranscript, startVoice, endVoice, onStreamingDelta, onStreamingComplete } = useVoiceMode({
  userInput,
  addUserMessage,
  onVoiceDelta,
  onVoiceComplete,
  onVoiceToolCall,
  getConversationId,
});
const chatContainer = ref(null);
const userName = ref("");
const isTyping = ref(false);
const pagination = ref({ current: 1, pageSize: 25, total: 0 });
const loading = ref(false);
const selectedImage = ref(null);
const showImageModal = ref(false);
let topMessage = null;

const conversationId = ref(null);
const currentTurnId = ref(null);
const hasDigitalTwin = ref(false);
const twinName = ref("");
const dmGroupId = ref(null);
const isStreaming = ref(false);
const streamingMessage = ref("");
const currentToolCall = ref(null);
const streamingJsonData = ref(null);
const streamingImages = ref([]);
const streamingWebSearchUsed = ref(false);
const streamingStartTime = ref(null);
const thoughtTime = ref(null);
const streamingToolsUsed = ref([]);
const expandedThoughts = ref({});
let _nextMsgId = 0;

const pushMessage = (msg) => {
  msg._id = ++_nextMsgId;
  chatMessages.value.push(msg);
};

const toggleThoughtExpand = (msgId) => {
  expandedThoughts.value[msgId] = !expandedThoughts.value[msgId];
};

const getOrCreateConversationId = () => {
  let storedId = sessionStorage.getItem(CONVERSATION_ID_KEY);
  if (!storedId) {
    storedId = crypto.randomUUID();
    sessionStorage.setItem(CONVERSATION_ID_KEY, storedId);
  }
  return storedId;
};

const resetStreamingState = () => {
  isStreaming.value = false;
  streamingMessage.value = "";
  currentToolCall.value = null;
  streamingJsonData.value = null;
  streamingImages.value = [];
  streamingWebSearchUsed.value = false;
  currentTurnId.value = null;
  streamingStartTime.value = null;
  streamingToolsUsed.value = [];
};


const stripEmojis = (text) => {
  if (!text) return text;
  return text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]/gu, '').trim();
};

const handleSSEToolCalls = (data) => {
  const tools = data.tools || [];
  const runningTool = tools.find(tool => tool.status === 'running');
  if (runningTool) {
    const displayName = runningTool.display_name || runningTool.name || 'Processing...';
    const cleanName = stripEmojis(displayName);
    currentToolCall.value = cleanName;
    if (!streamingToolsUsed.value.includes(cleanName)) {
      streamingToolsUsed.value.push(cleanName);
    }
  }
};

const handleSSEDelta = (data) => {
  if (data.content) {
    currentToolCall.value = null;
    streamingMessage.value += data.content;
    scrollToBottom();
    // Feed chunk into the streaming TTS sentence pipeline
    onStreamingDelta(data.content);
  }
};

const handleSSEComplete = async (data) => {
  // Merge tools from complete event (sandbox sends tools_used array)
  if (data.tools_used && Array.isArray(data.tools_used)) {
    for (const tool of data.tools_used) {
      const cleanName = stripEmojis(tool);
      if (cleanName && !streamingToolsUsed.value.includes(cleanName)) {
        streamingToolsUsed.value.push(cleanName);
      }
    }
  }
  const finalMessage = data.answer || streamingMessage.value;
  const formattedMessage = streamingWebSearchUsed.value 
    ? finalMessage
    : convertMarkdownToHTML(finalMessage);
  
  let thinkingDuration = null;
  if (streamingStartTime.value) {
    thinkingDuration = Math.round((Date.now() - streamingStartTime.value) / 1000);
  }
  thoughtTime.value = thinkingDuration;
  
  let jsonData = null;
  if (data.table || data.chart) {
    jsonData = {};
      if (data.table) {
        jsonData.table = {
          column_names: data.table.header_rows.map(h => 
            typeof h === 'object' ? h.column_name : h
          ),
          rows: data.table.rows.map(row => 
            row.map(cell => cell === null ? "" : cell)
          )
        };
      }
    if (data.chart) {
      jsonData.chart = data.chart;
    }
  }
  
  const imagesToSave = [...streamingImages.value];
  const webSearchUsed = streamingWebSearchUsed.value;
  
  pushMessage({
    message: formattedMessage,
    json_data: jsonData,
    recommended_questions: [],
    images: imagesToSave,
    web_search_used: webSearchUsed,
    direction: "backward",
    sent_at: new Date(),
    thought_time: thinkingDuration,
    tools_used: [...streamingToolsUsed.value],
  });

  resetStreamingState();
  await scrollToBottom();

  // Flush any remaining sentence buffer now that SSE stream is complete
  onStreamingComplete();

  setTimeout(async () => {
    disconnectSSE();
    
    try {
      await DashboardService.saveAgiMessage(
        conversationId.value,
        currentTurnId.value,
        finalMessage,
        jsonData,
        recommendedQuestions.value,
        imagesToSave,
        webSearchUsed
      );
    } catch (error) {
      console.error("Failed to save message:", error);
    }
  }, 5000);
};

const handleSSERecommendations = (data) => {
  if (data.recommended_questions) {
    recommendedQuestions.value = data.recommended_questions;
  }
};

const handleSSEError = (data) => {
  console.error("SSE Error:", data.error);
  disconnectSSE();
  resetStreamingState();
  
  pushMessage({
    message: "Sorry, something went wrong. Please try again.",
    json_data: null,
    recommended_questions: [],
    images: [],
    web_search_used: false,
    direction: "backward",
    sent_at: new Date(),
  });
};

const handleSSEMaxRetries = () => {
  console.error("SSE max retries reached");
  resetStreamingState();
  
  pushMessage({
    message: "Connection lost. Please try sending your message again.",
    json_data: null,
    recommended_questions: [],
    images: [],
    web_search_used: false,
    direction: "backward",
    sent_at: new Date(),
  });
};

// Tool progress detection — gateway sends tool calls as emoji-prefixed messages
const TOOL_EMOJI_PATTERN = /^[\u{1F300}-\u{1F9FF}\u{2600}-\u{27BF}\u2699\uFE0F]/u;
const TOOL_CALL_PATTERN = /^(browser_|computer_|text_editor|bash|file_|skill_|screenshot|navigate|click|scroll|type|key|wait|mouse)/i;
const isToolProgressMessage = (text) => {
  if (!text) return false;
  const trimmed = text.trim();
  if (TOOL_EMOJI_PATTERN.test(trimmed)) return true;
  if (TOOL_CALL_PATTERN.test(trimmed)) return true;
  // Pure JSON payloads from tool calls (e.g. {"full": true}, {"url": "..."})
  if (trimmed.startsWith("{") && trimmed.endsWith("}") && trimmed.length < 500) {
    try { JSON.parse(trimmed); return true; } catch (_) {}
  }
  return false;
};

// Track message IDs sent by the frontend (to distinguish from twin responses)
const _sentMessageIds = new Set();
let _pollInterval = null;
let _pollTimeout = null;

// MQTT-based real-time message handler for the assistant group
const _mqttMessageHandler = (topic, payload) => {
  try {
    const event = JSON.parse(payload.toString());
    const eventType = event.event || event.type;
    const messageId = event.message_id;

    // Handle typing indicator
    if (topic.includes("/typing")) {
      if (isStreaming.value) {
        isTyping.value = true;
        setTimeout(() => { isTyping.value = false; }, 5000);
      }
      return;
    }

    // Handle new message in assistant group
    if (eventType === "NEW_MESSAGE" && messageId) {
      // Skip messages already seen (our own sends or already processed by polling)
      if (_sentMessageIds.has(messageId)) return;
      // Mark immediately to prevent polling from processing the same message
      _sentMessageIds.add(messageId);

      // Fetch the full message
      ChatService.fetchMessageById(messageId, dmGroupId.value).then((resp) => {
        if (!resp?.success || !resp?.message) return;
        const text = resp.message.text || resp.message.content || "";
        if (!text.trim()) return;

        // Skip error/tool messages
        const errorIndicators = ["Provider authentication", "API call failed", "Rate limit",
          "Max retries", "Non-retryable", "Retrying in", "No Codex"];
        if (errorIndicators.some(ind => text.includes(ind))) return;

        isTyping.value = false;

        if (isToolProgressMessage(text)) {
          const cleanName = stripEmojis(text.split(":")[0] || text);
          currentToolCall.value = cleanName;
          if (cleanName && !streamingToolsUsed.value.includes(cleanName)) {
            streamingToolsUsed.value.push(cleanName);
          }
        } else {
          // Final response
          const thinkingDuration = streamingStartTime.value
            ? Math.round((Date.now() - streamingStartTime.value) / 1000)
            : null;

          pushMessage({
            message: convertMarkdownToHTML(text),
            json_data: null,
            recommended_questions: [],
            images: [],
            web_search_used: false,
            direction: "backward",
            sent_at: new Date(resp.message.sent_at || Date.now()),
            thought_time: thinkingDuration,
            tools_used: [...streamingToolsUsed.value],
          });

          clearInterval(_pollInterval);
          clearTimeout(_pollTimeout);
          resetStreamingState();
          scrollToBottom();
        }
      }).catch(e => console.error("[AgiChat] fetch message error:", e));
    }
  } catch (e) {
    // Not JSON or parse error — ignore
  }
};

// Subscribe to the DM group's MQTT topics for real-time updates
const subscribeToDmGroup = () => {
  try {
    if (!dmGroupId.value) return;
    const wsId = WorkspaceService.getCurrentWorkspaceId();
    if (!wsId) return;

    const messagesTopic = Topics.groupMessages(wsId, dmGroupId.value);
    const typingTopic = Topics.groupTyping(wsId, dmGroupId.value);

    if (mqttService?.isConnected) {
      mqttService.subscribe(messagesTopic, _mqttMessageHandler);
      mqttService.subscribe(typingTopic, _mqttMessageHandler);
      console.log("[AgiChat] Subscribed to MQTT topics for group", dmGroupId.value);
    } else {
      console.warn("[AgiChat] MQTT not connected, will use polling fallback");
    }
  } catch (e) {
    console.warn("[AgiChat] MQTT subscription failed:", e);
  }
};

const unsubscribeFromDmGroup = () => {
  try {
    if (!dmGroupId.value) return;
    const wsId = WorkspaceService.getCurrentWorkspaceId();
    if (!wsId) return;

    const messagesTopic = Topics.groupMessages(wsId, dmGroupId.value);
    const typingTopic = Topics.groupTyping(wsId, dmGroupId.value);

    if (mqttService?.isConnected) {
      mqttService.unsubscribe(messagesTopic);
      mqttService.unsubscribe(typingTopic);
    }
  } catch (e) {
    console.warn("[AgiChat] MQTT unsubscribe failed:", e);
  }
};

const sendMessage = async () => {
  if (userInput.value.trim() === "") {
    return;
  }

  // Fallback: restore assistant group from sessionStorage
  if (!dmGroupId.value) {
    const stored = sessionStorage.getItem("twin_dm_group_id");
    if (stored) dmGroupId.value = Number(stored);
  }

  console.log("[AgiChat] sendMessage: dmGroupId=", dmGroupId.value);

  // If assistant group is set up, use chat messaging (same as regular chats)
  if (dmGroupId.value) {
    pushMessage({
      direction: "forward",
      message: userInput.value,
      sent_at: new Date(),
    });

    recommendedQuestions.value = [];
    resetStreamingState();
    isStreaming.value = true;
    streamingStartTime.value = Date.now();

    await scrollToBottom();
    const userQuestion = userInput.value;
    userInput.value = "";

    try {
      const sendResp = await ChatService.createMessage({
        recipient_type: 1,
        recipient_id: Number(dmGroupId.value),
        text: userQuestion,
      });
      // Track sent message ID so MQTT handler and polling skip it
      const sentMsgId = sendResp?.message?.id;
      if (sentMsgId) _sentMessageIds.add(sentMsgId);

      // Poll as fallback (MQTT may not be connected from I-Assistant page)
      clearInterval(_pollInterval);
      clearTimeout(_pollTimeout);
      const sentAt = Date.now();
      _pollInterval = setInterval(async () => {
        try {
          const resp = await ChatService.fetchGroupMessages(dmGroupId.value, 5, 0);
          const messages = resp?.messages || resp?.data || [];
          for (const msg of messages) {
            // Skip our own sent messages
            if (_sentMessageIds.has(msg.id)) continue;
            const msgTime = new Date(msg.sent_at || msg.created_at || 0).getTime();
            if (msgTime <= sentAt) continue;
            const text = msg.text || msg.content || "";
            if (!text.trim()) continue;
            // Skip errors (same list as MQTT handler)
            if (["Provider authentication", "API call failed", "Rate limit",
              "Max retries", "Non-retryable", "Retrying in", "No Codex"].some(e => text.includes(e))) continue;

            _sentMessageIds.add(msg.id); // prevent MQTT handler from duplicating

            // Skip tool progress messages (e.g. "🔍 Searching...") — not the final answer
            if (isToolProgressMessage(text)) {
              const cleanName = stripEmojis(text.split(":")[0] || text);
              currentToolCall.value = cleanName;
              if (cleanName && !streamingToolsUsed.value.includes(cleanName)) {
                streamingToolsUsed.value.push(cleanName);
              }
              continue;
            }

            // Got a final response from the twin
            clearInterval(_pollInterval);
            clearTimeout(_pollTimeout);
            const thinkingDuration = streamingStartTime.value ? Math.round((Date.now() - streamingStartTime.value) / 1000) : null;
            pushMessage({
              message: convertMarkdownToHTML(text),
              direction: "backward",
              sent_at: new Date(msg.sent_at || Date.now()),
              thought_time: thinkingDuration,
              tools_used: [...streamingToolsUsed.value],
            });
            resetStreamingState();
            scrollToBottom();
            return;
          }
        } catch (e) { /* retry */ }
      }, 2000);
      // Timeout after 15 minutes
      _pollTimeout = setTimeout(() => { clearInterval(_pollInterval); if (isStreaming.value) { resetStreamingState(); pushMessage({ message: "Response timed out.", direction: "backward", sent_at: new Date() }); } }, 900000);
    } catch (error) {
      console.error("Failed to send message:", error);
      resetStreamingState();
      pushMessage({
        message: "Unable to send message. Please try again.",
        direction: "backward",
        sent_at: new Date(),
      });
    }
    return;
  }

  // Fallback: use the existing SSE flow (non-twin users)
  pushMessage({
    direction: "forward",
    message: userInput.value,
    sent_at: new Date(),
  });

  recommendedQuestions.value = [];
  resetStreamingState();
  isStreaming.value = true;
  streamingStartTime.value = Date.now();

  await scrollToBottom();
  const userQuestion = userInput.value;
  userInput.value = "";

  try {
    const currentUrl = window.location.href;
    conversationId.value = getOrCreateConversationId();

    const objectInfo = getObjectInfoFromURL();
    const objectType = objectInfo?.objectType || null;
    const objectId = objectInfo?.objectId || null;

    const response = await DashboardService.sendAgiMessageStreaming(
      userQuestion,
      conversationId.value,
      currentUrl,
      objectType,
      objectId
    );

    if (response.success) {
      currentTurnId.value = response.turn_id;

      connectSSE(response.sse_url, {
        [SSE_EVENTS.TOOL_CALLS]: handleSSEToolCalls,
        [SSE_EVENTS.DELTA]: handleSSEDelta,
        [SSE_EVENTS.COMPLETE]: handleSSEComplete,
        [SSE_EVENTS.RECOMMENDATIONS]: handleSSERecommendations,
        [SSE_EVENTS.ERROR]: handleSSEError,
        onMaxRetriesReached: handleSSEMaxRetries,
      });
    } else {
      console.error("Failed to initiate streaming:", response.error_message);
      resetStreamingState();

      pushMessage({
        message: response.error_message || "Unable to process your request.",
        direction: "backward",
        sent_at: new Date(),
      });
    }
  } catch (error) {
    console.error("Failed to send message:", error);
    resetStreamingState();

    pushMessage({
      message: "Unable to process your request at the moment. Please try again later.",
      direction: "backward",
      sent_at: new Date(),
    });
  }
};

const handleFileAttach = (event) => {
  // TODO: implement file attachments via chat service
  const files = event.target.files;
  if (files?.length) {
    console.log("[AgiChat] File attach not yet implemented:", files.length, "files");
  }
};

const handleKeyDown = (event) => {
  if (event.key === "Enter") {
    if (event.shiftKey) {
      return;
    }
    event.preventDefault();
    sendMessage();
  }
};

const getAllMessages = async () => {
  try {
    // If DM group exists, load from chat service
    if (dmGroupId.value) {
      try {
        const resp = await ChatService.fetchGroupMessages(Number(dmGroupId.value), 100, 0);
        const messages = resp?.messages || resp?.data || [];
        console.log("[AgiChat] Loaded", messages.length, "messages from group", dmGroupId.value);

        // Filter out error spam and system messages
        const errorPatterns = ["Provider authentication", "API call failed", "Rate limit",
          "Max retries", "Non-retryable", "Retrying in", "No Codex", "Encrypted content"];

        const messages_arr = messages
          .filter((msg) => {
            const text = msg.text || msg.content || "";
            if (!text.trim()) return false;
            if (msg.is_system) return false;
            if (errorPatterns.some(p => text.includes(p))) return false;
            if (isToolProgressMessage(text)) return false;
            return true;
          })
          .map((msg) => ({
            message: convertMarkdownToHTML(msg.text || msg.content || ""),
            // Direction: twin messages have message_type="twin_response", user messages don't
            direction: (msg.message_type === "twin_response") ? "backward" : "forward",
            sent_at: msg.sent_at || msg.created_at,
            json_data: null,
            recommended_questions: [],
            images: [],
            web_search_used: false,
          })).reverse();

        console.log("[AgiChat] After filtering:", messages_arr.length, "messages to display");
        messages_arr.forEach(msg => { msg._id = ++_nextMsgId; });
        chatMessages.value.unshift(...messages_arr);
        pagination.value.total = messages_arr.length;
      } catch (e) {
        console.error("[AgiChat] Failed to load messages:", e);
      }
      return;
    }

    // Fallback: load from old AI chat table
    const response = await DashboardService.getAgiMessages(
      pagination.value.current - 1,
      pagination.value.pageSize,
    );
    if (response.success) {
      const messages_arr = response.messages.map((message) => {
        let jsonData = message.json_data;

        if (jsonData?.table?.header_rows) {
          jsonData = {
            ...jsonData,
            table: {
              column_names: jsonData.table.header_rows.map(h =>
                typeof h === 'object' ? h.column_name : h
              ),
              rows: jsonData.table.rows
            }
          };
          if (jsonData.chart) {
            jsonData.chart = message.json_data.chart;
          }
        } else if (jsonData?.table?.column_names && jsonData?.table?.rows) {
          const expectedLength = jsonData.table.column_names.length;
          jsonData.table.rows = jsonData.table.rows.map(row => {
            if (row.length < expectedLength) {
              const paddedRow = [...row];
              while (paddedRow.length < expectedLength) {
                paddedRow.push("");
              }
              return paddedRow;
            }
            return row;
          });
        }

        return {
          ...message,
          message: message.web_search_used
            ? message.message
            : convertMarkdownToHTML(message.message),
          json_data: jsonData,
          recommended_questions: message.recommended_questions || [],
          images: message.images || [],
          web_search_used: message.web_search_used || false,
        };
      });
      chatMessages.value.unshift(...messages_arr);
      userName.value =
        response.user_name.trim() === ""
          ? response.user_email
          : response.user_name;
      pagination.value.total = response.total || chatMessages.value.length;

      if (messages_arr.length > 0) {
        const mostRecentMessage = messages_arr.reduce((latest, current) => {
          return new Date(current.sent_at) > new Date(latest.sent_at) ? current : latest;
        });
        recommendedQuestions.value = mostRecentMessage.recommended_questions || [];
      }
    }
  } catch (error) {
    console.error("Error fetching chat messages:", error);
  }
};

const loadMoreMessages = async () => {
  if (
    pagination.value.current * pagination.value.pageSize >=
      pagination.value.total ||
    loading.value
  ) {
    return;
  }
  loading.value = true;
  pagination.value.current += 1;

  try {
    const response = await DashboardService.getAgiMessages(
      pagination.value.current - 1,
      pagination.value.pageSize,
    );
    if (response.success) {
      const messages_arr = response.messages.map((message) => {
        let jsonData = message.json_data;
        
        if (jsonData?.table?.header_rows) {
          jsonData = {
            ...jsonData,
            table: {
              column_names: jsonData.table.header_rows.map(h => 
                typeof h === 'object' ? h.column_name : h
              ),
              rows: jsonData.table.rows
            }
          };
          if (jsonData.chart) {
            jsonData.chart = message.json_data.chart;
          }
        } else if (jsonData?.table?.column_names && jsonData?.table?.rows) {
          const expectedLength = jsonData.table.column_names.length;
          jsonData.table.rows = jsonData.table.rows.map(row => {
            if (row.length < expectedLength) {
              const paddedRow = [...row];
              while (paddedRow.length < expectedLength) {
                paddedRow.push("");
              }
              return paddedRow;
            }
            return row;
          });
        }
        
        return {
          ...message,
          message: convertMarkdownToHTML(message.message),
          json_data: jsonData,
        };
      });
      chatMessages.value.unshift(...messages_arr);
    } else {
      console.error(
        "Failed to fetch more chat messages:",
        response.error_message,
      );
    }
  } catch (error) {
    console.error("Error fetching more chat messages:", error);
  } finally {
    loading.value = false;
    await observeChats();
  }
};

const insertEmoji = (emoji) => {
  const insert = emoji.i;
  let tArea = document.getElementById(textareaId);
  if (0 === insert) {
    return;
  }

  let startPos = tArea.selectionStart,
    endPos = tArea.selectionEnd,
    cursorPos = startPos,
    tmpStr = tArea.value;

  userInput.value =
    tmpStr.substring(0, startPos) +
    insert +
    tmpStr.substring(endPos, tmpStr.length);
  setTimeout(() => {
    cursorPos += insert.length;
    tArea.selectionStart = tArea.selectionEnd = cursorPos;
  }, 10);
  tArea.focus();
};

const scrollToBottom = async () => {
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
};

const observer = new IntersectionObserver(
  async (entries) => {
    if (entries[0].isIntersecting) {
      await loadMoreMessages();
    }
  },
  {
    root: chatContainer.value,
    threshold: 0.1,
  },
);

watch(chatMessages, () => {
  scrollToBottom();
});

const formatMessage = (message) => {
  if (!message) return "";
  return message;
};

const handleMessageClick = (event) => {
  const target = event.target;

  if (target.tagName === 'IMG') {
    event.preventDefault();
    window.open(target.src, '_blank');
    return;
  }

  if (target.tagName === 'A' && target.href) {
    const href = target.getAttribute('href');

    if (href) {
      event.preventDefault();

      if (href.startsWith('/')) {
        if (href.startsWith('/market')) {
          const currentRoute = router.currentRoute.value.path;
          if (currentRoute !== '/market' && !currentRoute.startsWith('/market/')) {
            store.commit('appStore/SET_LAST_ROUTE', currentRoute);
          }
        }

        router.push(href);
      } else {
        window.open(target.href, '_blank');
      }
    }
  }
};

const sendRecommendedQuestion = (question) => {
  userInput.value = question;
  recommendedQuestions.value = [];
};

const openImageModal = (imageUrl) => {
  selectedImage.value = imageUrl;
  showImageModal.value = true;
};

const closeImageModal = () => {
  showImageModal.value = false;
  selectedImage.value = null;
};

onMounted(async () => {
  conversationId.value = getOrCreateConversationId();

  // Check if user has a digital twin
  try {
    const twinStatus = await DashboardService.getTwinStatus();
    if (twinStatus.has_digital_twin) {
      hasDigitalTwin.value = true;
      twinName.value = twinStatus.twin_name || "Digital Twin";

      // Get the assistant group ID (stored on the digital employee)
      if (twinStatus.assistant_group_id) {
        dmGroupId.value = Number(twinStatus.assistant_group_id);
        sessionStorage.setItem("twin_dm_group_id", String(dmGroupId.value));
        subscribeToDmGroup();
        console.log("[AgiChat] Assistant group:", dmGroupId.value);
      }
    }
  } catch (e) {
    console.warn("Failed to check twin status:", e);
  }

  await getAllMessages();

  // Show welcome message only on first-ever visit to i-assistant in this workspace
  const wsId = WorkspaceService.getCurrentWorkspaceId();
  const welcomeKey = `ia_welcome_shown_${wsId}`;
  if (chatMessages.value.length === 0 && !localStorage.getItem(welcomeKey)) {
    localStorage.setItem(welcomeKey, "1");
    const userFirstName = store.getters["auth/userData"]?.first_name || "there";
    pushMessage({
      message: `Hey ${userFirstName} 👋 welcome to SuperAGI!<br><br>` +
        `I'm I-Assistant, your single hub to prospect leads, enrich data, run an AI-native CRM, fire off email campaigns, call contacts, power support, and stacks more.<br><br>` +
        `To get you value fast, what do you want to do first?<br><br>` +
        `• "Find ICP accounts and new leads"<br>` +
        `• "Launch targeted Email Marketing campaigns with Value prop at scale"<br>` +
        `• "Spin up AI-powered multi-channel sequences (email, calls, LinkedIn, WhatsApp)"<br>` +
        `• "Set up Dialer and calling workflows"<br>` +
        `• "I'm here to consolidate tools & cut costs"<br><br>` +
        `Or just tell me your goal in your own words — I'll spin up a plan for you.<br><br>` +
        `Quick win: Check out our <a href="/market" style="color: #3B82F6; text-decoration: underline;">Market</a>, where you can install apps like AI Native CRM, Sequences, Dialer, Meeting Intelligence, Signals, and more.<br><br>` +
        `👉 <a href="/market" style="color: #3B82F6; text-decoration: underline; font-weight: 600;">Open Market</a><br><br>` +
        `Need help getting set up? Meet our <a href="/meet/superagi-onboarding-assistant" style="color: #3B82F6; text-decoration: underline;">Onboarding Assistant</a>`,
      direction: "backward",
      sent_at: new Date(),
    });
  }

  await scrollToBottom();
  await observeChats();
  let tArea = document.getElementById(textareaId);
  if (tArea) {
    tArea.focus();
  }

  const draftMessage = localStorage.getItem("homepage_draft_message");
  if (draftMessage) {
    userInput.value = draftMessage;
    localStorage.removeItem("homepage_draft_message");
  }

  intervalId = setInterval(() => {
    currentTime.value = new Date();
  }, 60000);
});

const observeChats = async () => {
  await nextTick();
  
  if (!chatContainer.value) {
    console.error("Chat container not found.");
    return;
  }
  
  topMessage = chatContainer.value.lastElementChild;
  if (topMessage) {
    observer.observe(topMessage);
  } else {
    console.error("Top message element not found.");
  }
};

onBeforeUnmount(() => {
  observer.disconnect();
  clearInterval(intervalId);
  clearInterval(_pollInterval);
  clearTimeout(_pollTimeout);
  unsubscribeFromDmGroup();
  disconnectSSE();
  endVoice();
});
</script>

<style scoped>
.agi-chat-container {
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.agi-chat-container.drawer-mode {
  height: 100%;
}

.chat-messages-area {
  display: flex;
  flex-direction: column-reverse;
  min-height: 0;
  flex: 1 1 0;
  overflow-y: auto;
}

.streaming-status {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 500;
  line-height: 20px;
  background: linear-gradient(90deg, #6b7280, #9ca3af, #6b7280);
  background-size: 200% 100%;
  animation: gradient-sweep 1.5s ease-in-out infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@keyframes gradient-sweep {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.formatted_message {
  line-height: 1.5rem;
}

.formatted_message :deep(p) {
  margin: 0 !important;
  padding: 0 !important;
}

.formatted_message :deep(a) {
  color: var(--link-color, #818cf8);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.formatted_message :deep(a:hover) {
  opacity: 0.8;
}

.dots::after {
  content: '';
  animation: dots 1.5s steps(3, end) infinite;
}

@keyframes dots {
  0%   { content: ''; }
  33%  { content: '.'; }
  66%  { content: '..'; }
  100% { content: '...'; }
}

.recommended_wrapper {
  background: var(--background-color);
  border-radius: 50px;
  border: 1px solid var(--white-opacity-20);
  transition: all 0.15s ease;
}

.recommended_wrapper:hover {
  background: var(--white-opacity-4);
  transform: translateX(4px);
}

.recommendation-stagger {
  opacity: 0;
  transform: translateY(8px);
  animation: stagger-in 0.3s ease forwards;
}

@keyframes stagger-in {
  to { opacity: 1; transform: translateY(0); }
}

.message-enter {
  animation: message-slide-in 0.25s ease-out;
}

@keyframes message-slide-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.thought-time-container {
  display: flex;
  flex-direction: column;
}

.thought-time-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--secondary-color, #9ca3af);
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
}

.thought-time-label .expand-icon {
  font-size: 14px;
  transition: transform 0.2s ease;
  display: inline-block;
}

.thought-time-label .expand-icon.expanded {
  transform: rotate(90deg);
}

.thought-tools-list {
  margin-top: 4px;
  padding-left: 4px;
  border-left: 2px solid var(--white-opacity-8, rgba(255,255,255,0.08));
  display: flex;
  flex-direction: column;
  gap: 2px;
  animation: tools-expand 0.2s ease-out;
}

.thought-tool-item {
  font-size: 12px;
  font-weight: 400;
  color: var(--secondary-color, #9ca3af);
  padding: 2px 8px;
}

@keyframes tools-expand {
  from { opacity: 0; max-height: 0; }
  to { opacity: 1; max-height: 500px; }
}

.action_properties_wrapper_emoji .ant-tooltip-inner {
  color: #181819 !important;
  background-color: transparent !important;
  box-shadow: none !important;
  width: 340px;
  margin-top: 25px;
  margin-left: -100px;
}

/* ── Voice End button (purple gradient pill) ──────────────────── */
.voice_end_btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px 6px 10px;
  border-radius: 8px;
  background: var(--action-button);
  color: var(--white-color);
  border: none;
  cursor: pointer;
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  font-weight: 600;
  transition: opacity 0.15s ease;
  white-space: nowrap;
}

.voice_end_btn:hover {
  opacity: 0.88;
}

/* ── Waveform bars ────────────────────────────────────────────── */
.waveform_bars {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 16px;
}

.waveform_bars .bar {
  display: block;
  width: 3px;
  border-radius: 2px;
  background: var(--white-color);
  animation: waveform-pulse 0.9s ease-in-out infinite;
}

.waveform_bars .bar:nth-child(1) { height: 6px;  animation-delay: 0s; }
.waveform_bars .bar:nth-child(2) { height: 12px; animation-delay: 0.15s; }
.waveform_bars .bar:nth-child(3) { height: 8px;  animation-delay: 0.3s; }
.waveform_bars .bar:nth-child(4) { height: 14px; animation-delay: 0.45s; }

@keyframes waveform-pulse {
  0%, 100% { transform: scaleY(1); opacity: 0.7; }
  50%       { transform: scaleY(1.6); opacity: 1; }
}

/* ── Transcribing label (live transcript in user bubble) ─────── */
</style>
